import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../hooks/useProjectStore';
import { useT } from '../i18n';
import './DashboardPage.css';

const MODULES = [
  { key: '/datahub',    icon: '▣', label: '数据中心',  desc: '统一导入、单位标准化、曲线审核与应用' },
  { key: '/logging',    icon: '∿', label: '单井测井',  desc: '数据中心应用后的标准测井曲线' },
  { key: '/formation',  icon: '▤', label: '地层绘制',  desc: '地层结构建模与可视化' },
  { key: '/sweetspot',  icon: '◇', label: '甜点评价',  desc: '储层甜点识别与评级' },
  { key: '/fracture',   icon: '⚡', label: '压裂设计', desc: '压裂方案设计与段簇优化' },
  { key: '/fracsim',    icon: '✸', label: '压裂模拟', desc: 'PKN缝网扩展模拟' },
  { key: '/warning',    icon: '△', label: '工况预警',  desc: '实时监测与预警' },
  { key: '/economics',  icon: '▦', label: '经济评价',  desc: '投资效益评估' },
  { key: '/mechanics',  icon: '●', label: '岩石力学',  desc: '力学参数剖面' },
  { key: '/formation/3d', icon: '◈', label: '3D模型',   desc: '三维地质模型' },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { currentProject, clearProject } = useProjectStore();
  const t = useT();

  if (!currentProject) {
    navigate('/projects');
    return null;
  }

  const p = currentProject;
  const depth = p.total_depth ?? p.derived_total_depth ?? null;

  const statusMap: Record<string, { label: string; color: string; bg: string }> = {
    draft:     { label: '草稿', color: '#8598AD', bg: '#F0F3F6' },
    active:    { label: '进行中', color: '#27864A', bg: '#E8F5ED' },
    completed: { label: '已完成', color: '#2874A6', bg: '#E8F0F8' },
    archived:  { label: '已归档', color: '#8598AD', bg: '#F0F3F6' },
  };
  const st = statusMap[p.status] || statusMap.draft;

  return (
    <div className="dashboard">
      {/* Project Header */}
      <header className="dashboard-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ margin: 0 }}>{p.well_name}</h1>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 4,
              background: st.bg, color: st.color, fontWeight: 600,
            }}>{t(st.label)}</span>
          </div>
          <p className="header-sub" style={{ margin: 0 }}>
            {p.name} · {p.field_name}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-outline" onClick={() => { clearProject(); navigate('/projects'); }}>
            {t('← 切换项目')}
          </button>
          <button className="btn-outline">{t('项目设置')}</button>
          <button className="btn-primary" onClick={() => navigate('/datahub/import')}>{t('导入数据')}</button>
        </div>
      </header>

      {/* Project Quick Stats */}
      <div className="stats-row">
        {[
          // 井深与目的层位: 项目档案里填了就用填的, 没填就用**从数据推导**的值。
          // 这两个数字是数据的结论 —— 导入井轨迹和地层之后它们才该出现,
          // 在那之前显示"-"是诚实的, 不该预先填好让人以为平台算出来了。
          { label: '井型', value: p.well_type ? t(p.well_type) : '-', unit: '' },
          {
            label: '总井深',
            value: depth != null ? depth.toLocaleString() : '-',
            unit: 'm',
          },
          { label: '目标层位', value: p.target_formation || p.derived_target_formation || '-', unit: '' },
          // 不再写 `|| 1` —— 那个兜底把"字段没返回"和"真的只有 1 个成员"混成了一样,
          // 详情接口漏了 member_count 时它就一直显示 1, 谁也看不出是 bug。
          { label: '项目成员', value: p.member_count == null ? '-' : `${p.member_count}`, unit: '人' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-label">{t(s.label)}</div>
            <div className="stat-value">
              {s.value}<span className="stat-unit">{s.unit && t(s.unit)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Module Grid */}
      <section className="module-section">
        <h2>{t('功能模块')}</h2>
        <div className="module-grid">
          {MODULES.map(m => (
            <div
              key={m.key}
              className="module-card"
              onClick={() => navigate(m.key)}
            >
              <div className="module-icon">{m.icon}</div>
              <div className="module-info">
                <div className="module-label">{t(m.label)}</div>
                <div className="module-stat">{t(m.desc)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Dual Panel Preview */}
      <section className="panel-preview">
        <h2>{t('实时监测面板')}</h2>
        <div className="dual-panel">
          <div className="panel-left card" onClick={() => navigate('/formation')} style={{ cursor: 'pointer' }}>
            <div className="panel-title">
              <span className="title-dot" style={{ background: '#C2533A' }} />
              {t('地层剖面 + 井眼轨迹')}
            </div>
            <div className="panel-placeholder">
              <span>{t('点击进入地层可视化')}</span>
              <p>{t('查看 {well} 地层模型与井眼轨迹动画', { well: p.well_name })}</p>
            </div>
          </div>
          <div className="panel-right card" onClick={() => navigate('/sweetspot')} style={{ cursor: 'pointer' }}>
            <div className="panel-title">
              <span className="title-dot" style={{ background: '#2874A6' }} />
              {t('甜点评价 · 可压性分级')}
            </div>
            <div className="panel-placeholder">
              <span>{t('点击进入甜点评价')}</span>
              <p>{t('测井曲线 · 地质/工程/综合甜点 · Ⅰ-Ⅳ 可压性分级')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
